import { MessageType, PROTOCOL_VERSION, parseControlMessage } from '@relay/shared';

export interface ControlWsHandlers {
  onAck: (userId: string) => void;
  onAuthError: (code: string, message: string) => void;
  onSessionEnded: (reason: string) => void;
  onClose: (code: number, reason: string) => void;
  onError: (err: Event) => void;
  onCursorPosition?: (nx: number, ny: number) => void;
  // WebRTC signaling from the host (routed via the relay). The viewer
  // reacts to WEBRTC_OFFER by creating a PeerConnection and answering,
  // and to WEBRTC_ICE by addIceCandidate'ing against that connection.
  onWebRtcOffer?: (sdp: string) => void;
  onWebRtcIce?: (candidate: { candidate: string; sdpMid: string | null; sdpMLineIndex: number | null }) => void;
}

export interface ControlWs {
  sendInputMouse: (payload: unknown) => void;
  sendInputKey: (payload: unknown) => void;
  sendPing: () => void;
  // Reply to the host's offer with our SDP answer.
  sendWebRtcAnswer: (sessionId: number, sdp: string) => void;
  // Trickle our own ICE candidates back to the host. Pass null-fields
  // + empty candidate to signal end-of-candidates.
  sendWebRtcIce: (
    sessionId: number,
    candidate: { candidate: string; sdpMid: string | null; sdpMLineIndex: number | null },
  ) => void;
  close: () => void;
}

let messageCounter = 0;
function nextId(): string {
  messageCounter = (messageCounter + 1) >>> 0;
  return `c_${Date.now().toString(36)}_${messageCounter.toString(36)}`;
}

function envelope(type: string, payload: unknown): string {
  return JSON.stringify({
    v: PROTOCOL_VERSION,
    id: nextId(),
    ts: Date.now(),
    type,
    payload,
  });
}

export function openControlWs(
  url: string,
  sessionJwt: string,
  sessionId: number,
  handlers: ControlWsHandlers,
  signal?: AbortSignal,
): Promise<ControlWs> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('aborted'));
      return;
    }
    const ws = new WebSocket(url);
    let authed = false;
    const onAbort = () => { try { ws.close(1000, 'aborted'); } catch { /* ignore */ } };
    signal?.addEventListener('abort', onAbort, { once: true });

    const cleanup = () => {
      ws.removeEventListener('open', onOpen);
      ws.removeEventListener('message', onMessage);
      ws.removeEventListener('close', onClose);
      ws.removeEventListener('error', onError);
    };

    const onOpen = () => {
      ws.send(envelope(MessageType.AUTH_CLIENT_HELLO, {
        sessionJwt,
        sessionId,
        deviceToken: null,
        clientVersion: '0.1.0',
        capabilities: { codecs: ['jpeg'] },
      }));
    };

    const onMessage = (e: MessageEvent) => {
      let raw: unknown;
      try { raw = JSON.parse(typeof e.data === 'string' ? e.data : ''); }
      catch { return; }
      const parsed = parseControlMessage(raw);
      if (!parsed.ok) return;

      if (parsed.type === MessageType.AUTH_CLIENT_HELLO_ACK) {
        authed = true;
        const userId = (parsed.payload as { userId: string }).userId;
        handlers.onAck(userId);
        let sendCount = 0;
        const trySend = (label: string, body: string): void => {
          if (ws.readyState !== WebSocket.OPEN) {
            if (sendCount % 30 === 0) console.warn(`[ctrl-ws] skip ${label}: readyState=${ws.readyState} (expected 1)`);
            sendCount++;
            return;
          }
          if (sendCount % 30 === 0) console.log(`[ctrl-ws] send ${label} bytes=${body.length}`);
          sendCount++;
          ws.send(body);
        };
        resolve({
          sendInputMouse: (payload) => trySend('input.mouse', envelope(MessageType.INPUT_MOUSE, payload)),
          sendInputKey: (payload) => trySend('input.key', envelope(MessageType.INPUT_KEY, payload)),
          sendPing: () => trySend('ping', envelope(MessageType.PING, { nonce: nextId(), sentAt: Date.now() })),
          sendWebRtcAnswer: (sessionId, sdp) => trySend('webrtc.answer', envelope(MessageType.WEBRTC_ANSWER, { sessionId, sdp })),
          sendWebRtcIce: (sessionId, c) => trySend('webrtc.ice', envelope(MessageType.WEBRTC_ICE, { sessionId, ...c })),
          close: () => { try { ws.close(1000, 'client_disconnect'); } catch { /* ignore */ } },
        });
        return;
      }
      if (parsed.type === MessageType.AUTH_ERROR) {
        const p = parsed.payload as { code: string; message: string };
        handlers.onAuthError(p.code, p.message);
        if (!authed) reject(new Error(`auth_error:${p.code}`));
        return;
      }
      if (parsed.type === MessageType.SESSION_ENDED) {
        const p = parsed.payload as { reason?: string };
        handlers.onSessionEnded(p.reason ?? 'unknown');
        return;
      }
      if (parsed.type === MessageType.CURSOR_POSITION) {
        const p = parsed.payload as { nx: number; ny: number };
        handlers.onCursorPosition?.(p.nx, p.ny);
        return;
      }
      if (parsed.type === MessageType.WEBRTC_OFFER) {
        const p = parsed.payload as { sdp: string };
        handlers.onWebRtcOffer?.(p.sdp);
        return;
      }
      if (parsed.type === MessageType.WEBRTC_ICE) {
        const p = parsed.payload as { candidate: string; sdpMid: string | null; sdpMLineIndex: number | null };
        handlers.onWebRtcIce?.({ candidate: p.candidate, sdpMid: p.sdpMid, sdpMLineIndex: p.sdpMLineIndex });
        return;
      }
    };

    const onClose = (e: CloseEvent) => {
      cleanup();
      signal?.removeEventListener('abort', onAbort);
      handlers.onClose(e.code, e.reason);
      if (!authed) reject(new Error(`closed_before_auth:${e.code}`));
    };

    const onError = (e: Event) => {
      handlers.onError(e);
      if (!authed) reject(new Error('ws_error'));
    };

    ws.addEventListener('open', onOpen);
    ws.addEventListener('message', onMessage);
    ws.addEventListener('close', onClose);
    ws.addEventListener('error', onError);
  });
}
